/**
 * WizardFrame — single shared shell for the New Report wizard.
 *
 * Until Workflow Prompt 3 the wizard rendered three independent
 * `fixed inset-0` modals (ReportTypeSelectStep, ReportDisambiguation
 * Step, ReportPreferencesStep). That meant:
 *   - no shared frame ⇒ no breadcrumb / step strip,
 *   - each step re-mounted its own backdrop ⇒ flash on transition,
 *   - keyboard exits worked on some steps and not others,
 *   - focus traps + Tab cycling diverged across steps.
 *
 * This component is the shared frame. It renders a Base-UI Dialog
 * with:
 *   - one persistent backdrop (no flash on step transitions),
 *   - a header carrying the wizard title + a horizontal step strip
 *     ("Step N of M · A › B › C", active step bold),
 *   - a body slot for the active step's form,
 *   - an optional footer slot for the step's primary CTAs (Back /
 *     Continue / Generate).
 *
 * Escape and backdrop-click both fire `onCancel`. Focus trap and
 * Tab cycling are handled by Base-UI Dialog — we don't reimplement
 * them.
 */
import React from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { ChevronRight } from 'lucide-react';

interface Props {
  /** Controls the dialog open state. The frame closes on Escape /
   *  backdrop click via `onCancel` so the parent stays the source of
   *  truth for `open`. */
  open: boolean;
  /** Fired on Escape, on backdrop click, and on the dialog's close
   *  button. The next workflow prompt routes this through a
   *  `handleCancelAttempt` that may show a confirmation if the user
   *  has unsaved edits; for now the parent wires it straight to its
   *  own close handler. */
  onCancel: () => void;
  title: string;
  /** 1-based index of the active step. */
  currentStep: number;
  totalSteps: number;
  /** Length must equal `totalSteps`; the active label is bolded. */
  stepLabels: string[];
  children: React.ReactNode;
  /** Optional sticky footer for step CTAs. Renders after the body
   *  with a top border. */
  footer?: React.ReactNode;
}

export const WizardFrame: React.FC<Props> = ({
  open,
  onCancel,
  title,
  currentStep,
  totalSteps,
  stepLabels,
  children,
  footer,
}) => {
  // Base-UI Dialog calls onOpenChange(false) on Escape, backdrop
  // click, and the built-in close button. Forwarding to onCancel
  // unifies the three exit paths.
  const handleOpenChange = (next: boolean) => { if (!next) onCancel(); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        size="full"
        className="sm:max-w-[min(96vw,980px)] max-h-[90vh] p-0 gap-0 flex flex-col"
        showCloseButton={false}
        data-testid="wizard-frame"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border space-y-3 shrink-0">
          <DialogTitle className="text-sm font-bold uppercase tracking-widest text-foreground">
            {title}
          </DialogTitle>
          {/* Step strip — text-only progress indicator. Distinct
              fonts/weights so the active step pops; muted chevrons
              between labels. */}
          <div
            className="flex items-center gap-2 text-[10px]"
            data-testid="wizard-step-strip"
            aria-label={`Step ${currentStep} of ${totalSteps}`}
          >
            <span className="text-text-secondary uppercase tracking-widest font-bold shrink-0">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-text-muted">·</span>
            <ol className="flex items-center gap-1.5 flex-wrap min-w-0">
              {stepLabels.map((label, idx) => {
                const isActive = idx + 1 === currentStep;
                const isPast = idx + 1 < currentStep;
                return (
                  <React.Fragment key={`${idx}-${label}`}>
                    <li
                      data-testid={`wizard-step-${idx + 1}`}
                      data-active={isActive ? 'true' : 'false'}
                      aria-current={isActive ? 'step' : undefined}
                      className={`uppercase tracking-widest truncate ${
                        isActive
                          ? 'text-text-primary font-bold'
                          : isPast
                            ? 'text-text-secondary'
                            : 'text-text-muted'
                      }`}
                    >
                      {label}
                    </li>
                    {idx < stepLabels.length - 1 && (
                      <ChevronRight className="w-3 h-3 text-text-muted shrink-0" aria-hidden />
                    )}
                  </React.Fragment>
                );
              })}
            </ol>
          </div>
        </div>

        {/* Body — scrolls when content overflows the dialog cap. */}
        <div className="flex-1 overflow-y-auto" data-testid="wizard-frame-body">
          {children}
        </div>

        {/* Optional footer for step CTAs. */}
        {footer && (
          <div
            className="px-5 py-3 border-t border-border bg-muted/30 shrink-0"
            data-testid="wizard-frame-footer"
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
